import { sanitizeUrl, semanticVar } from '@libs/core';
import { getRebranding } from '../../hooks/useRebranding';
import { getSemanticColorByVarName, getSemanticColors } from '../../theme/colors';
import { EWidgetVersion } from '../../types/enums';
import { isCssColor } from '../../utils/colorUtils';
import { resolveVersion } from '../../utils/widgetVersion';

/** Icon folder of every supported version. A new version adds its folder here; a dropped one is removed. */
const iconFolderByVersion: Readonly<Record<EWidgetVersion, string>> = {
    [EWidgetVersion.V1]: 'svg',
    [EWidgetVersion.V2]: 'rebrandingSvg',
};

const iconVersions = Object.keys(iconFolderByVersion).map(Number) as EWidgetVersion[];

/**
 * Resolves the full URL to an SVG symbol sprite for the given icon name.
 *
 * @param name icon name (e.g. 'ic_alert_circle_small')
 * @param version `version` of the widget showing the icon: no version → the oldest folder, a supported one → its
 *                folder, any other → the latest folder (see {@link resolveVersion}). Required on purpose, so a
 *                widget cannot silently get the icons of another version. Temporary: no version while the
 *                rebranding switch is on → v2 icons, like `isV2` / `getSemanticColors` (see consumer_conventions.md).
 */
export const getHref = (name: string | undefined, version: number | null | undefined) => {
    const folder = iconFolderByVersion[!version && getRebranding() ? EWidgetVersion.V2 : resolveVersion(version, iconVersions)];
    return name ? `${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/consumer3/${folder}/${name}.svg#symb` : undefined;
};

const isUrlOrPath = (value: string): boolean => {
    return /[^a-zA-Z0-9-_]/.test(value);
};

export const getBackgroundHref = (name?: string) => {
    if (!name) {
        return undefined;
    }
    if (isUrlOrPath(name)) {
        return sanitizeUrl(name);
    }
    return `${process.env.NX_PUBLIC_CLOUD_PAYSEND_API}/consumer3/mobile/${name}.png`;
};

// Holds a strong reference to every preloaded icon: a garbage-collected Image can have its in-flight request dropped.
const preloadedIcons = new Map<string, HTMLImageElement>();

/**
 * Warms the browser HTTP cache with an icon so it still renders when the network is gone.
 * Loads the exact URL CoreImageView uses, so the cached entry is reused as-is (no fetch/Cache API involved).
 * A failed attempt is forgotten so a later call can retry it.
 *
 * @param name icon name as passed to getHref (e.g. 'ic_alert_circle_small')
 * @param version design version to load the icon for (see {@link getHref}). When omitted, the icon is preloaded
 *                for every version, for callers that do not know which design will show it.
 */
export const preloadIcon = (name?: string, version?: number) => {
    if (!name || typeof window === 'undefined') {
        return;
    }
    if (typeof version === 'number') {
        const href = getHref(name, version);
        if (!href || preloadedIcons.has(href)) {
            return;
        }
        const image = new window.Image();
        preloadedIcons.set(href, image);
        image.onerror = () => preloadedIcons.delete(href);
        image.src = href;
        return;
    }
    iconVersions.forEach((iconVersion) => preloadIcon(name, iconVersion));
};

export const resetPreloadedIconsForTesting = (): void => {
    preloadedIcons.clear();
};

/**
 * A CSS custom property name, with or without the leading `--` (e.g. `purple_100`, `--purple_100`).
 * `/` is allowed because semantic tokens arrive path-like (`fill/primary/default`); it is normalized to `_`.
 */
const CSS_VAR_NAME = /^(?:--)?[a-z_][a-z0-9_/-]*$/i;

/**
 * Normalizes a color option into a value usable in CSS.
 *
 * A color can only arrive as hex or `rgb()`/`rgba()` — anything else is treated as a CSS variable name:
 *
 * - `#fff`, `rgba(0, 0, 0, 0.5)` → returned unchanged;
 * - an already wrapped `var(…)` → returned unchanged;
 * - `purple_100` → `var(--purple_100)`, `red` → `var(--red)`;
 * - a semantic token, in any spelling (`fill/primary/default`, its unprefixed variable with or without `var()`),
 *   resolves to that token in the design `version` via `getSemanticColors` (`var(--v2_…)` for v2, `var(--v1_…)` otherwise);
 * - an empty value, or a name that is not a valid custom property ident, yields `undefined` so the
 *   caller can fall back instead of emitting a broken declaration.
 *
 * @param value color or CSS variable name coming from widget options
 * @param version design version of the widget the color is for
 */
export const resolveCssColor = (value: string | undefined, version: number | undefined): string | undefined => {
    const normalized = value?.trim();
    if (!normalized) {
        return undefined;
    }
    if (isCssColor(normalized)) {
        return normalized;
    }
    const wrapped = /^var\((--[a-z0-9_-]+)\)$/i.exec(normalized)?.[1];
    const name = wrapped ?? normalized;
    if (CSS_VAR_NAME.test(name)) {
        const semantic = getSemanticColorByVarName(`--${(name.startsWith('--') ? name.slice(2) : name).replaceAll('/', '_')}`);
        if (semantic) {
            return getSemanticColors(version)[semantic];
        }
    }
    if (/^var\(--[a-z0-9_-]+(?:\s*,[^;{}]*)?\)$/i.test(normalized)) {
        return normalized;
    }
    if (!CSS_VAR_NAME.test(normalized)) {
        return undefined;
    }
    return semanticVar(normalized.startsWith('--') ? normalized.slice(2) : normalized);
};
